import React, {Dispatch, SetStateAction, useContext, useEffect, useState} from "react";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import {
    AccessoryLgWidth,
    LeaderboardRowHeight,
    LeaderboardRowHeightCompact,
    AccessoryXlWidth,
    ViewMyDashboardWidth,
    PanelState
} from "./accessoryConstants";
import DesktopAccessoryPanel from "./DesktopAccessoryPanel";
import MobileAccessoryPanel from "./MobileAccessoryPanel";
import {TimeUnit, useCountdown} from "../../hooks/useCountdown";
import {Role} from "engage-common/types";
import {triviaStateDuration, TriviaStatus} from "../../state/state";
import {EngageAppContext} from "../../App";
import {HostBubbleContext} from "../../contexts/HostBubbleContext";

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
    accessoryPanel: {
        zIndex: 5,
        [theme.breakpoints.up("lg")] : {
            width: props => props.showLeaderboard ? AccessoryLgWidth + ViewMyDashboardWidth : AccessoryLgWidth,
            position: "relative",
            [theme.breakpoints.only("xl")] : {
                width: props => props.showLeaderboard ? AccessoryXlWidth + ViewMyDashboardWidth : AccessoryXlWidth,
            },
        },
        [theme.breakpoints.down("md")]: {
            position:"fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: LeaderboardRowHeightCompact + 8,
            [theme.breakpoints.up("sm")] : {
                height: LeaderboardRowHeight + 8,
            },
        }
    },
}));

type AccessoryPanelProps = {
    panelState: PanelState
    setPanelState: Dispatch<SetStateAction<PanelState>>
    sharedDisplay: boolean
}

export default function AccessoryPanel({panelState,setPanelState, sharedDisplay}:AccessoryPanelProps) {
    const theme:Theme = useTheme();
    const {appState, userState, user} = useContext(EngageAppContext.current);
    const {setRefreshPosition} = useContext(HostBubbleContext);
    const [showAnswerWarning, setShowAnswerWarning] = useState(false);

    const countdown = useCountdown(appState.common.timeMark, triviaStateDuration(appState.common), appState.common.pauseMark, TimeUnit.SECONDS);

    useEffect(() => {
        if(appState.common.state === TriviaStatus.Complete && user?.role !== Role.host) {
            setPanelState(PanelState.expanded);
        }
        if((appState.common.state === TriviaStatus.QuestionSubmit
            || (appState.common.state === TriviaStatus.QuestionStart && countdown <= 3)) &&
            (userState.responses.length <= appState.common.currentIndex &&
                (panelState === PanelState.expanded || panelState === PanelState.expanding))) {
                setShowAnswerWarning(true);
        } else {
            setShowAnswerWarning(false);
        }
    }, [countdown, appState.common.state, userState.responses, appState.common.currentIndex, panelState]);

    const toggleSlide = () => {
        setPanelState(current => {
           switch(current) {
               case PanelState.collapsed:
                   return PanelState.expanding;
               case PanelState.expanded:
                   return PanelState.collapsing;
               case PanelState.collapsing:
                   return PanelState.expanding;
               case PanelState.expanding:
                   return PanelState.collapsing;
           }
        });
    };

    const transitionEnd = (event:React.TransitionEvent<HTMLDivElement>) => {
        if(event.propertyName === "width" || event.propertyName === "height") {
            setRefreshPosition(true);
            setPanelState(current => {
                switch(current) {
                    case PanelState.collapsing:
                        return PanelState.collapsed;
                    case PanelState.expanding:
                        return PanelState.expanded;
                    default:
                        return current;
                }
            });
        }
    };

    const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
    const classes = useStyles({
        showLeaderboard: appState.common.gameOptions.showLeaderboard
    });

    return <div className={classes.accessoryPanel}>
        { isLarge ?
            <DesktopAccessoryPanel panelState={panelState} transitionEnd={transitionEnd}
                                   toggleSlide={toggleSlide} showAnswerWarning={showAnswerWarning}
                                   sharedDisplay={sharedDisplay} />
            :
            <MobileAccessoryPanel panelState={panelState} transitionEnd={transitionEnd}
                                  toggleSlide={toggleSlide} showAnswerWarning={showAnswerWarning}/>
        }
    </div>;
}